import {
    CreateMultiLegShippingEstimateByFuelResponse,
    EmissionFactorEstimate,
    MultiLegShippingEmissionEstimate,
    PassengerTransportationEmissionEstimate,
    SingleShippingEmissionEstimate,
    TransactionDocumentEmissionEstimate,
    TransactionEmissionEstimate,
} from '@lune-climate/lune'
import { SuccessResponseWithMeta } from '@lune-climate/lune/esm/core/SuccessResponse'
import { Leg, resolvedLegToApiLeg, useErrorSnackbar } from '@lune-fe/lune-components-lib'
import { useEffect, useState } from 'react'

import { asSuccessResponseWithMeta } from 'endpoints/util'
import { useLuneClient } from 'hooks/useLuneClient'
import { SnackbarMessages } from 'SnackbarMessages'
import { EstimateType, EstimateTypeName } from 'views/CalculateEmissions'

type EstimateUnion =
    | SuccessResponseWithMeta<SingleShippingEmissionEstimate>
    | SuccessResponseWithMeta<MultiLegShippingEmissionEstimate>
    | SuccessResponseWithMeta<CreateMultiLegShippingEstimateByFuelResponse>
    | SuccessResponseWithMeta<TransactionEmissionEstimate>
    | SuccessResponseWithMeta<EmissionFactorEstimate>
    | SuccessResponseWithMeta<PassengerTransportationEmissionEstimate>
    | SuccessResponseWithMeta<TransactionDocumentEmissionEstimate>
    | null

export const useEstimate = (calculationId: string | null, estimateType: EstimateType) => {
    const { enqueueSnackbar: snackbar } = useErrorSnackbar()
    const [estimate, setEstimate] = useState<EstimateUnion>(null)
    const [resolvedLegs, setResolvedLegs] = useState<Leg[]>([])
    const [loading, setLoading] = useState(true)
    const luneClient = useLuneClient()

    useEffect(() => {
        if (calculationId === null) {
            return
        }
        // eslint-disable-next-line complexity
        const fetchEstimate = async () => {
            setLoading(true)
            try {
                let result

                // Fetch based on estimate type
                switch (estimateType) {
                    case 'shipping': {
                        const shippingResult = await luneClient.getShippingEstimate(calculationId)
                        if (shippingResult.isOk()) {
                            const est = asSuccessResponseWithMeta<SingleShippingEmissionEstimate>(
                                shippingResult.value,
                            )
                            if (est.resolvedLegs) {
                                setResolvedLegs(
                                    est.resolvedLegs.map((resolvedLeg) =>
                                        resolvedLegToApiLeg(
                                            resolvedLeg,
                                            shippingResult.value.convertedShipment,
                                            shippingResult.value.shipmentConversionMethod,
                                        ),
                                    ),
                                )
                            }
                            setEstimate(est)
                            result = shippingResult
                        }
                        break
                    }
                    case 'multi_leg_shipping':
                        result = await luneClient.getMultiLegShippingEstimate(calculationId)
                        if (result.isOk()) {
                            setEstimate(
                                asSuccessResponseWithMeta<MultiLegShippingEmissionEstimate>(
                                    result.value,
                                ),
                            )
                        }
                        break
                    case 'multi_leg_shipping_by_fuel':
                        result = await luneClient.getMultiLegShippingEstimateByFuel(calculationId)
                        if (result.isOk()) {
                            setEstimate(
                                asSuccessResponseWithMeta<CreateMultiLegShippingEstimateByFuelResponse>(
                                    result.value,
                                ),
                            )
                        }
                        break
                    case 'transaction':
                        result = await luneClient.getTransactionEstimate(calculationId)
                        if (result.isOk()) {
                            setEstimate(
                                asSuccessResponseWithMeta<TransactionEmissionEstimate>(
                                    result.value,
                                ),
                            )
                        }
                        break
                    case 'activity':
                        result = await luneClient.getEmissionFactorEstimate(calculationId)
                        if (result.isOk()) {
                            setEstimate(
                                asSuccessResponseWithMeta<EmissionFactorEstimate>(result.value),
                            )
                        }
                        break
                    case 'passenger_transportation':
                        result = await luneClient.getPassengerTransportationEstimate(calculationId)
                        if (result.isOk()) {
                            setEstimate(
                                asSuccessResponseWithMeta<PassengerTransportationEmissionEstimate>(
                                    result.value,
                                ),
                            )
                        }
                        break
                    case 'transaction_document':
                        result = await luneClient.getTransactionDocumentEstimate(calculationId)
                        if (result.isOk()) {
                            setEstimate(
                                asSuccessResponseWithMeta<TransactionDocumentEmissionEstimate>(
                                    result.value,
                                ),
                            )
                        }
                        break
                    default:
                        throw new Error(`Unsupported estimate type: ${estimateType}`)
                }

                if (result === undefined) {
                    snackbar(SnackbarMessages.GENERIC_ERROR)
                } else if (result.isErr()) {
                    snackbar(SnackbarMessages.GENERIC_ERROR, { error: result.error })
                }
            } catch (err) {
                console.error(`Failed to fetch ${estimateType} estimate:`, err)
                snackbar(`Failed to load ${EstimateTypeName(estimateType)} details`, { error: err })
            } finally {
                setLoading(false)
            }
        }

        fetchEstimate()
    }, [calculationId, estimateType, luneClient, snackbar])

    return { estimate, resolvedLegs, loading }
}
